import React, { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react';
import { ArrowUpDown, 
  Pencil, 
  X, 
  Save, 
  Trash,
  BookOpen
} from 'lucide-react'
import { saveTask, deleteTask, createTask, setCurrentTask } from '../../reducers/taskReducer'
import { deleteThread } from '../../reducers/chatReducer'
import { createTaskService, updateTaskService, deleteTaskService, deleteTaskFileService } from '../../utils/taskService'
import { deleteChatService } from '../../utils/chatService'
import { formatDate, formatEstimation, formatDateInput } from '../../utils/formatter'
import ConfirmationDialog from '../Shared/ConfirmationDialog'

const TaskTable = React.forwardRef(({ currentProject, projectTasks, categoryId, isCategoryView }, ref) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getToken, userId } = useAuth();

  const chats = useSelector(state => state.chats || []);
  const [sortColumn, setSortColumn] = useState('sortOrder')
  const [sortDirection, setSortDirection] = useState('asc')
  const [editingTask, setEditingTask] = useState(null)
  const [newTask, setNewTask] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const taskChatToDelete = chats.find(chat => chat.taskId === taskToDelete?.id);

  const tasks = projectTasks;

  const handleAddNewTask = () => {
    const emptyTask = {
      id: `task_${Math.random().toString(36).substring(2, 15)}`,
      title: 'New Task',
      description: '',
      startBy: new Date(),
      endBy: new Date(),
      estimation: 60,
      sortOrder: sortedTasks.length + 1,
      completed: false
    }
    
    setNewTask(emptyTask)
    handleEdit(emptyTask)
  }

  const handleDelete = async () => {
    if (taskChatToDelete) {
      const fileId = taskChatToDelete.fileId;

      await deleteTaskFileService(getToken, { fileId });
      await deleteChatService(getToken, taskChatToDelete.id);
      dispatch(deleteThread({ chatId: taskChatToDelete.id }));
    }    
    await deleteTaskService(getToken, taskToDelete.id);
    dispatch(deleteTask(taskToDelete.id))
    setIsDialogOpen(false); // Close the dialog after confirming
  };

  const openDeleteDialog = (task) => {
    setTaskToDelete(task);
    setIsDialogOpen(true);
  };

  React.useImperativeHandle(ref, () => ({
    callHandleAddNewTask() {
      handleAddNewTask();
    }
  }));
  
  const getTaskStatus = (task) => {
    const taskChatExists = chats.some(chat => chat.taskId === task.id);

    if (task.completed) {
      return "Done"
    }
    else if (taskChatExists) {
      return "In Progress"
    }
    else {
      return "Start";
    }
  }

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      if (sortColumn === 'sortOrder') {
        if (a.sortOrder !== b.sortOrder) {
          return sortDirection === 'asc' ? a.sortOrder - b.sortOrder : b.sortOrder - a.sortOrder
        }
        return sortDirection === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
      }
      
      if (sortColumn === 'startBy' || sortColumn === 'endBy') {
        return sortDirection === 'asc' 
          ? new Date(a[sortColumn]) - new Date(b[sortColumn])
          : new Date(b[sortColumn]) - new Date(a[sortColumn])
      }
      
      if (sortDirection === 'asc') {
        return a[sortColumn] > b[sortColumn] ? 1 : -1
      }
      return a[sortColumn] < b[sortColumn] ? 1 : -1
    })
  }, [tasks, sortColumn, sortDirection])

  const toggleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const handleTaskClick = ({task, start}) => {
    // const autoStart = start && getTaskStatus(task) !== "Done";
    dispatch(setCurrentTask(task.id))
    navigate(`/task/${task.id}`)

    // setSelectedTask(task.id)

    // setSelectedTask(task)
    // setTaskAutoStart(autoStart)
    // setIsDrawerOpen(true)
  }

  const handleEdit = (task) => {
    setEditingTask({
      ...task,
      estimationValue: task.estimation >= 1440 ? Math.floor(task.estimation / 1440) : task.estimation >= 60 ? Math.floor(task.estimation / 60) : task.estimation,
      estimationUnit: task.estimation >= 1440 ? 'days' : task.estimation >= 60 ? 'hours' : 'mins'
    })
  }

  const handleSave = async () => {
    const { estimationValue, estimationUnit, ...taskWithoutEstimationFields } = editingTask;

    const updatedTaskJson = {
      ...taskWithoutEstimationFields,
      metaData: {
        projectId: currentProject.id,
        projectTitle: currentProject.title,
      },
      estimation: editingTask.estimationUnit === 'hours' 
        ? editingTask.estimationValue * 60 
        : editingTask.estimationUnit === 'days' 
          ? editingTask.estimationValue * 1440 
          : editingTask.estimationValue
    }

    if (newTask && newTask.id === updatedTaskJson.id) {
      const createdTask = await createTaskService(getToken, userId, updatedTaskJson);
      dispatch(createTask( createdTask ))
      setNewTask(null)
    } else {
      const updatedTask = await updateTaskService(getToken, userId, updatedTaskJson);
      dispatch(saveTask({ task: updatedTask }))
    }
    setEditingTask(null)
  }

  const handleCancel = () => {
    setEditingTask(null)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setEditingTask(prev => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setEditingTask(prev => ({ ...prev, [name]: new Date(value) }))
  }

  const confirmationMessage = (task) => {
    return (
      <>
        <p>Are you sure you want to delete this task?</p>
        <p className="font-semibold mb-5">{task?.title}</p>
        <p>This action cannot be undone.</p>
      </>
    )
  }

  return (
    <div className=''>
      <table className="w-full mx-auto">
        
        <thead className="sticky top-0 border-gray-200 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100">
          <tr className="bg-muted/50">
            {!isCategoryView && (
              <th className="w-[5%] border-stone-200 border-0 border-r" onClick={() => toggleSort('sortOrder')} role="button">
                <div className="flex items-center">
                  <ArrowUpDown className="h-4 w-4" />
                </div>
              </th>   
            )}
            <th className="w-[60%] border-stone-200 border-0 border-r" onClick={() => toggleSort('title')} role="button">
              <div className="flex items-center gap-2 p-3">
                Title
                <ArrowUpDown className="h-4 w-4" />
              </div>
            </th>
            <th className="w-[10%] border-stone-200 border-0 border-r" onClick={() => toggleSort('startBy')} role="button">
              <div className="flex items-center gap-2 p-3">
                Start by
                <ArrowUpDown className="h-4 w-4" />
              </div>
            </th>
            <th className="w-[10%] border-stone-200 border-0 border-r" onClick={() => toggleSort('endBy')} role="button">
              <div className="flex items-center gap-2 p-3">
                End by
                <ArrowUpDown className="h-4 w-4" />
              </div>
            </th>
            <th className="w-[10%] border-stone-200 border-0 border-r">
              <div className="flex items-center gap-2 p-3">
                Estimation
              </div>
            </th>
            <th className="w-[5%] border-0"></th>
          </tr>
        </thead>

        <tbody>
          {newTask && (
            <tr className="hover:bg-muted/50">
              {!isCategoryView && (
                <td className='p-3 border border-stone-200'>
                  <input
                    type="number"
                    name="sortOrder"
                    value={editingTask.sortOrder}
                    onChange={handleInputChange}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Sort_Order_Change"
                    data-amp-track-description="Sort Order Change"  
                    className="p-1 border rounded w-11"
                  />
                </td>
              )}
              <td className="py-4 p-3 border border-stone-200">
                <input
                  type="text"
                  name="title"
                  value={editingTask.title}
                  onChange={handleInputChange}
                  data-amp-track-section="Task Table"
                  data-amp-track-action="Title_Change"
                  data-amp-track-description="Title Change"
                  className="w-full p-1 h-10 mb-1 border rounded"
                />
                <textarea
                  value={editingTask.description}
                  name="description"
                  onChange={handleInputChange}
                  data-amp-track-section="Task Table"
                  data-amp-track-action="Description_Change"
                  data-amp-track-description="Description Change"
                  placeholder="Type your description here..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                  rows="3"
                />
              </td>
              <td className='p-3 border border-stone-200'>
                <input
                  type="date"
                  name="startBy"
                  value={formatDateInput(editingTask.startBy)}
                  onChange={handleDateChange}
                  data-amp-track-section="Task Table"
                  data-amp-track-action="Start_By_Change"
                  data-amp-track-description="Start By Change"
                  className="p-1 border rounded"
                />
              </td>
              <td className='p-3 border border-stone-200'>
                <input
                  type="date"
                  name="endBy"
                  value={formatDateInput(editingTask.endBy)}
                  onChange={handleDateChange}
                  data-amp-track-section="Task Table"
                  data-amp-track-action="End_By_Change"
                  data-amp-track-description="End By Change"
                  className="p-1 border rounded"
                />
              </td>
              <td className='p-3 border border-stone-200'>
                <div className="flex items-center">
                  <input
                    type="number"
                    name="estimationValue"
                    value={editingTask.estimationValue}
                    onChange={handleInputChange}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Estimation_Value_Change"
                    data-amp-track-description="Estimation Value Change"
                    className="w-11 p-1 border rounded mr-2"
                  />
                  <select
                    name="estimationUnit"
                    value={editingTask.estimationUnit}
                    onChange={handleInputChange}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Estimation_Unit_Change"
                    data-amp-track-description="Estimation Unit Change"
                    className="p-1 border rounded"
                  >
                    <option value="mins">mins</option>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                  </select>
                </div>
              </td>
              <td className='p-3 border border-stone-200'>
                <div className="flex items-center">
                  <button
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Save_Click"
                    data-amp-track-description="Save Task"
                    className="flex items-center px-4 py-1 bg-green-500 hover:bg-green-600 rounded-full border-2 border-green-500 mr-2"
                    onClick={handleSave}>
                      <Save size={12} /> 
                      <span className="ml-2 text-sm">Save</span>
                  </button>
                  <button
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Cancel_Click"
                    data-amp-track-description="Cancel Task"
                    className="px-2 py-2 bg-red-500 hover:bg-red-600 rounded-full border-2 border-red-500"
                    onClick={() => {
                      setNewTask(null);
                      setEditingTask(null);
                    }}>
                      <X size={12} />
                  </button>
                </div>
              </td>
            </tr>
          )}


          {sortedTasks.map((task, index) => (
            <tr key={index} className={`${getTaskStatus(task) === "Done" && "text-stone-400"} hover:bg-muted/50`}>
              {!isCategoryView && (
              <td className="py-4 p-3 border-l-0 border-r border-t border-b border-stone-200">
                  {editingTask && editingTask.id === task.id ? (
                    <input
                      type="number"
                      name="sortOrder"
                      value={editingTask.sortOrder}
                      onChange={handleInputChange}
                      data-amp-track-section="Task Table"
                      data-amp-track-action="Sort_Order_Change"
                      data-amp-track-description="Sort Order Change"  
                      className="p-1 border rounded w-11"
                    />
                  ) : (
                    task.sortOrder
                  )}
                </td>
              )}
              <td className="py-4 p-3 border-l-0 border-r border-t border-b border-stone-200">
                {editingTask && editingTask.id === task.id ? (
                  <>
                    <div className="flex flex-row">
                      <input
                        type="text"
                        name="title"
                        value={editingTask.title}
                        onChange={handleInputChange}
                        data-amp-track-section="Task Table"
                        data-amp-track-action="Title_Change"
                        data-amp-track-description="Title Change"
                        className="w-full p-1 h-10 mb-1 border rounded"
                      />
                      <button
                        className="bg-stone-500 hover:bg-stone-600 rounded-xl px-2 py-0 my-2 border-2 border-stone-500"
                        onClick={() => openDeleteDialog(task)}
                        data-amp-track-section="Task Table"
                        data-amp-track-action="Delete_Task_Click"
                        data-amp-track-description="Delete Task"
                      >
                          <Trash size={16} />
                      </button>
                    </div>

                    <textarea
                      value={editingTask.description}
                      name="description"
                      onChange={handleInputChange}
                      data-amp-track-section="Task Table"
                      data-amp-track-action="Description_Change"
                      data-amp-track-description="Description Change"
                      placeholder="Describe your task here..."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                      rows="3"
                    />
                  </>
                ) : (
                  <div className="space-y-1 cursor-pointer hover:text-amber-900 active:text-gray-500" 
                    onClick={() => {
                      if (currentProject?.step !== 'in_progress') {
                        dispatch(setCurrentTask(task.id))
                        navigate(`/task/${task.id}`)
                      } else {
                        handleTaskClick({task, start: false})
                      }
                    }}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="View_Task_Click"
                    data-amp-track-description="View Task"
                  >
                    {isCategoryView && task.metaData?.projectId && (
                      <p className="flex items-center gap-1 mb-1 text-xs text-slate-700">
                        <BookOpen size={16} />
                        {task.metaData?.projectTitle}
                      </p>
                    )}

                    <div className="font-semibold text-lg">{task.title}</div>
                    {!isCategoryView && task.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 border-t border-t-stone-200 pt-2" title={task.description}>{task.description}</p>
                    )}
                    {isCategoryView && (
                      task.metaData?.projectCategory?.filter(category => category.categoryId === categoryId)
                        .map((category) => (
                          <div key={category.categoryId} className="text-xs text-muted-foreground border-t border-t-stone-200 pt-2">
                            {category.reason}
                          </div>
                        ))
                    )}
                  </div>
                )}
              </td>
              <td className='p-3 border border-stone-200'>
                {editingTask && editingTask.id === task.id ? (
                  <input
                    type="date"
                    name="startBy"
                    value={formatDateInput(editingTask.startBy)}
                    onChange={handleDateChange}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="Start_By_Change"
                    data-amp-track-description="Start By Change"
                    className="p-1 border rounded"
                  />
                ) : (
                  task.startBy ? formatDate(task.startBy) : "---"
                )}
              </td>
              <td className='p-3 border border-stone-200'>
                {editingTask && editingTask.id === task.id ? (
                  <input
                    type="date"
                    name="endBy"
                    value={formatDateInput(editingTask.endBy)}
                    onChange={handleDateChange}
                    data-amp-track-section="Task Table"
                    data-amp-track-action="End_By_Change"
                    data-amp-track-description="End By Change"
                    className="p-1 border rounded"
                  />
                ) : (
                  task.endBy ? formatDate(task.endBy) : "---"
                )}
              </td>
              <td className='p-3 border border-stone-200'>
                {editingTask && editingTask.id === task.id ? (
                  <div className="flex items-center">
                    <input
                      type="number"
                      name="estimationValue"
                      value={editingTask.estimationValue}
                      onChange={handleInputChange}
                      data-amp-track-section="Task Table"
                      data-amp-track-action="Estimation_Value_Change"
                      data-amp-track-description="Estimation Value Change"
                      className="w-11 p-1 border rounded mr-2"
                    />
                    <select
                      name="estimationUnit"
                      value={editingTask.estimationUnit}
                      onChange={handleInputChange}
                      data-amp-track-section="Task Table"
                      data-amp-track-action="Estimation_Unit_Change"
                      data-amp-track-description="Estimation Unit Change"
                      className="p-1 border rounded"
                    >
                      <option value="mins">mins</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                ) : (
                  formatEstimation(task.estimation)
                )}
              </td>
              <td className='p-3 border-r-0 border-l border-t border-b border-stone-200'>
                  <div className="flex justify-end items-center pr-2">
                    {editingTask && editingTask.id === task.id ? (
                      <div className="flex items-center">
                        <button
                          className="flex items-center px-4 py-1 bg-green-500 hover:bg-green-600 rounded-full border-2 border-green-500 mr-2"
                          onClick={handleSave}
                          data-amp-track-section="Task Table"
                          data-amp-track-action="Save_Click"
                          data-amp-track-description="Save Task"
                        >
                            <Save size={12} /> 
                            <span className="ml-2 text-sm">Save</span>
                        </button>
                        <button
                          className="px-2 py-2 bg-red-500 hover:bg-red-600 rounded-full border-2 border-red-500"
                          onClick={handleCancel}
                          data-amp-track-section="Task Table"
                          data-amp-track-action="Cancel_Click"
                          data-amp-track-description="Cancel Task"
                        >
                            <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        { currentProject?.step === 'in_progress' && (
                          <button
                            className={`${getTaskStatus(task) === "Done" ? "bg-stone-400" : "bg-stone-500"} text-smx hover:bg-amber-800 rounded-full px-4 py-1  border-2 border-stone-500 mr-2`}
                            onClick={() => handleTaskClick({task, start: true})}
                            data-amp-track-section="Task Table"
                            data-amp-track-action="Task_Action_Click"
                            data-amp-track-description={`${getTaskStatus(task)} Task Action`}
                          >
                            {getTaskStatus(task)}
                          </button>
                        )}
                        {getTaskStatus(task) !== "Done" && (
                          <button
                            className="px-2 py-2 bg-stone-200 text-stone-900 hover:text-white hover:bg-amber-800 rounded-full border-4 border-stone-900"
                            onClick={() => handleEdit(task)}
                            data-amp-track-section="Task Table"
                            data-amp-track-action="Edit_Task_Click"
                            data-amp-track-description="Edit Task"
                          >
                              <Pencil size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table> 
      
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleDelete}
        message={confirmationMessage(taskToDelete)}
        ampSection="Task Table"
      />
    </div>
  )
})

export default TaskTable;